import React, { FC, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import ImageCropper from './ImageCropper';
import { authApi } from '../../../services/authApi';
import ImageIcon from '@mui/icons-material/Image';
import { styled } from '@mui/system';
import browserImageCompression from 'browser-image-compression';

const Input = styled('input')({
  display: 'none',
});

interface ProfilePictureDialogProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess: (newUrl: string) => void;
  currentPicture?: string;
}

/**
 * Fluxo:
 * 1) usuário seleciona arquivo -> exibe preview e recorte
 * 2) ao confirmar recorte, enviamos via FormData para POST/patch em `/users/me/profile-picture`
 * 3) se ok, chamamos onUploadSuccess(urlRetornada) e fechamos
 */
const ProfilePictureDialog: FC<ProfilePictureDialogProps> = ({
  open,
  onClose,
  onUploadSuccess,
  currentPicture,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Quando o usuário escolhe um arquivo local
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        if (reader.result) {
          setSelectedImage(reader.result as string);
          uploadError && setUploadError(null);
        }
      });
      reader.readAsDataURL(file);
    }
  };

  // Cancelar o recorte
  const handleCancelCrop = () => {
    setSelectedImage(null);
    setUploadError(null);
  };

  // Recebemos o `File` recortado; agora fazemos o upload real
  const handleCropComplete = async (croppedFile: File) => {
    setUploading(true);
    setUploadError(null);
    try {
      // Faz compressão adicional antes do upload (opcional)
      const compressedFile = await browserImageCompression(croppedFile, {
        maxSizeMB: 1,
        maxWidthOrHeight: 512,
        useWebWorker: true,
      });

      // Monta FormData para enviar ao backend
      const formData = new FormData();
      formData.append('profile_picture', compressedFile, 'profile_picture.jpg');

      // Envia para rota de upload
      const response = await authApi.post('/users/me/profile-picture', formData);
      // Supondo que retorne { url: '/uploads/...' }
      const { url } = response.data;
      onUploadSuccess(url);

      // Fecha o dialog
      handleCancelCrop();
      onClose();
    } catch (err: any) {
      console.error('Erro ao fazer upload da imagem:', err);
      setUploadError('Falha ao fazer upload da imagem. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Atualizar Foto de Perfil</DialogTitle>
      <DialogContent>
        {/* Se não houver imagem selecionada ainda, mostra o botão para escolher */}
        {!selectedImage ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              p: 2,
            }}
          >
            <label htmlFor="upload-button">
              <Input accept="image/*" id="upload-button" type="file" onChange={handleFileChange} />
              <Button variant="contained" component="span" startIcon={<ImageIcon />}>
                Selecionar Imagem
              </Button>
            </label>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Escolha uma imagem para atualizar sua foto de perfil.
            </Typography>
            {uploadError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {uploadError}
              </Typography>
            )}
          </Box>
        ) : (
          // Caso já tenha imagem selecionada, exibimos o componente de recorte
          <ImageCropper
            imageSrc={selectedImage}
            onCropComplete={handleCropComplete}
            onCancel={handleCancelCrop}
          />
        )}
      </DialogContent>
      <DialogActions>
        {/* Se não tiver nenhuma imagem selecionada para recorte, podemos fechar */}
        {!selectedImage && (
          <Button onClick={onClose} disabled={uploading}>
            Cancelar
          </Button>
        )}
        {/* Se já tiver imagem e estivermos no recorte, oferecemos outro botão "Cancelar" */}
        {selectedImage && (
          <Button onClick={handleCancelCrop} disabled={uploading}>
            Cancelar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProfilePictureDialog;